/**
 * ATL-1831 — the manufacture dual-write payload's ingredient lines.
 *
 * A saved manufacture is priced from each line's stored `purchasePrice` (+ `packagePrice`)
 * (`calculateIngredientPurchasePrice`); the Firestore line carries them, the Supabase line did not,
 * so an org reading Supabase saw every saved ingredient re-priced at today's catalogue cost.
 *
 * The backend validates both as numbers (`@IsNumber()`, null allowed), and a 400 is a permanent
 * outbox failure — the manufacture would be lost from Supabase entirely. Firestore product prices are
 * not reliably numbers (an empty string and numeric strings both occur), so each cost is sent in one
 * of three states, the rule the backend's `recordedIngredientCost` reads back:
 *   - absent    — the line has no such key (not recorded: priced at today's catalogue cost);
 *   - a number  — recorded (a numeric string is sent as its number — the cost it records, though the
 *                 Firestore screens concatenate it: `("100" || 0) + 0` is "1000"; none is stored today);
 *   - null      — the key is present without a usable number; the app prices that cost at 0
 *                 (`(value || 0) + …`), and null keeps that reading where dropping the key would not.
 */

const recordedCost = (line: Record<string, unknown>, key: string): number | null | undefined => {
  if (!Object.prototype.hasOwnProperty.call(line, key) || line[key] === undefined) return undefined
  const value = line[key]
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value
  return typeof n === 'number' && Number.isFinite(n) ? n : null
}

export interface ManufactureIngredientPayload {
  productId: string
  quantity: number
  name?: string
  purchasePrice?: number | null
  packagePrice?: number | null
  productUnit?: string
}

export const manufactureIngredientPayload = (
  ingredient: Record<string, unknown>,
): ManufactureIngredientPayload => {
  const purchasePrice = recordedCost(ingredient, 'purchasePrice')
  const packagePrice = recordedCost(ingredient, 'packagePrice')
  return {
    productId: ingredient.id as string,
    quantity: Number(ingredient.quantity),
    name: ingredient.name as string | undefined,
    ...(purchasePrice !== undefined ? { purchasePrice } : {}),
    ...(packagePrice !== undefined ? { packagePrice } : {}),
    ...(typeof ingredient.productUnit === 'string' && ingredient.productUnit !== ''
      ? { productUnit: ingredient.productUnit }
      : {}),
  }
}
